import axios from 'axios';
import useBillingStore from '../store/useBillingStore';

class ApiService {
  constructor() {
    this.baseURL = process.env.REACT_APP_BACKEND_URL;

    // Create axios instance
    this.api = axios.create({
      baseURL: this.baseURL,
      timeout: 120000,
    });

    // Add request interceptor to include auth headers
    this.api.interceptors.request.use(
      async (config) => {
        const urlParams = new URLSearchParams(window.location.search);
        const apiKey = urlParams.get('api_key') || localStorage.getItem('uttik_api_key');

        if (apiKey) {
          config.headers['X-API-KEY'] = apiKey;
          // When using API key, we don't necessarily have a Clerk session,
          // so we skip the standard Authorization header if API key is present
        } else if (window.Clerk && window.Clerk.session) {
          const token = await window.Clerk.session.getToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }

        // Add organization context if available
        const orgId = urlParams.get('org_id') || localStorage.getItem('uttik_organization_id') || (window.Clerk && window.Clerk.organization ? window.Clerk.organization.id : null);
        if (orgId) {
          config.headers['X-Organization-ID'] = orgId;
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling and token refresh
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // If 401 error and we haven't retried yet
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            // Force refresh the token by skipping cache
            if (window.Clerk && window.Clerk.session) {
              const freshToken = await window.Clerk.session.getToken({ skipCache: true });
              if (freshToken) {
                originalRequest.headers.Authorization = `Bearer ${freshToken}`;
                return this.api(originalRequest);
              }
            }
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
            // If refresh fails, redirect to login
            window.location.href = '/sign-in';
            return Promise.reject(refreshError);
          }
        }

        const errorMessage = error.response?.data?.message || error.message;
        const statusCode = error.response?.status;

        // Log API errors (exclude 401s as they are handled by refresh logic or redirect)
        if (statusCode !== 401) {
          this.logError(
            `API Error (${statusCode}): ${errorMessage}\nURL: ${originalRequest.url}`,
            "ApiService",
            statusCode >= 500 ? "High" : "Medium"
          );
        }

        console.error('API Error:', error.response?.data || error.message);
        return Promise.reject(error);
      }
    );
  }

  // Authentication endpoints
  async validateToken() {
    const response = await this.api.post('/api/auth/validate');
    return response.data;
  }

  // User management
  async getUserProfile() {
    const response = await this.api.get('/api/user/profile');
    return response.data;
  }

  async updateUserProfile(profileData) {
    const response = await this.api.put('/api/user/profile', profileData);
    return response.data;
  }

  async uploadContent(formData) {
    const response = await this.api.post('/api/content/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    useBillingStore.getState().fetchBillingInfo();
    return response.data;
  }

  async uploadWidgetLogo(formData) {
    const response = await this.api.post('/api/widgets/upload-logo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getContentList() {
    const response = await this.api.get('/api/content/list');
    return response.data;
  }

  async getSpecificContent(contentId) {
    const response = await this.api.get(`/api/content/${contentId}`);
    return response.data;
  }

  async deleteContent(contentId) {
    const response = await this.api.delete(`/api/content/${contentId}`);
    return response.data;
  }


  async deleteWidget(widgetId) {
    try {
      const response = await this.api.delete(`/api/widgets/${widgetId}`);
      return response.data;
    } catch (error) {
      // Handle HTTP errors (404, 500, etc.)
      if (error.response) {
        // Server responded with error status
        throw new Error(
          error.response.data.message ||
          error.response.data.suggestion ||
          'Failed to delete widget'
        );
      } else if (error.request) {
        // Network error
        throw new Error('Network error while deleting widget');
      } else {
        // Other error
        throw new Error('An unexpected error occurred while deleting widget');
      }
    }
  }

  // FAQ management
  async getFAQs(params = {}) {
    const response = await this.api.get('/api/faqs/list', { params });
    return response.data;
  }

  async createFAQ(data) {
    const response = await this.api.post('/api/faqs/create', data);
    return response.data;
  }

  async bulkUpdateFAQs(payload) {
    const response = await this.api.post('/api/faqs/bulk-update', payload);
    return response.data;
  }

  // Tag Management
  async getTags() {
    const response = await this.api.get('/api/tags/list');
    return response.data;
  }

  async autocompleteTags(query) {
    const response = await this.api.get('/api/tags/autocomplete', { params: { query } });
    return response.data;
  }

  async createTag(tag) {
    // Assuming the API expects { tag: "tag_name" } or just the tag string in a field
    const response = await this.api.post('/api/tags/create', { tag });
    return response.data;
  }

  // Category Management
  async getCategories() {
    const response = await this.api.get('/api/categories/');
    return response.data;
  }

  async createCategory(categoryData) {
    // categoryData should contain: name, slug (optional), description (optional)
    const response = await this.api.post('/api/categories/create', categoryData);
    return response.data;
  }

  async deleteCategory(categoryId) {
    const response = await this.api.delete(`/api/categories/${categoryId}`);
    return response.data;
  }

  async seedCategories() {
    const response = await this.api.post('/api/categories/seed');
    return response.data;
  }

  // Analytics & Review Dashboard
  async getReviewDashboardData() {
    // Get organization_id from Clerk
    const organizationId = window.Clerk?.organization?.id;
    if (!organizationId) {
      throw new Error('No organization selected');
    }

    const response = await this.api.get('/api/faqs/review_dashboard', {
      params: { organization_id: organizationId }
    });
    return response.data;
  }

  async submitFeedback(faqId, isHelpful) {
    const response = await this.api.post('/api/faqs/feedback', {
      faq_id: faqId,
      is_helpful: isHelpful
    });
    return response.data;
  }

  async findContentOpportunities() {
    const response = await this.api.post('/api/tasks/find-opportunities');
    return response.data;
  }

  async flagFAQsForReview() {
    const response = await this.api.post('/api/tasks/flag-for-review');
    return response.data;
  }

  async findLinkOpportunities() {
    const response = await this.api.post('/api/tasks/find-link-opportunities');
    return response.data;
  }

  async optimizeFAQ(faqData) {
    // Expect: [{ faq_id, optimization_type, question, answer }]
    if (!Array.isArray(faqData)) {
      throw new Error('optimizeFAQ expects an array payload');
    }
    const cleaned = faqData.map(item => ({
      faq_id: String(item.faq_id),
      optimization_type: item.optimization_type,
      question: item.question,
      answer: item.answer,
    }));
    if (cleaned.length === 0) {
      throw new Error('No FAQs selected for optimization');
    }
    if (cleaned.length > 50) {
      throw new Error('You can optimize at most 50 FAQs at once');
    }
    try {
      const response = await this.api.post('/api/faqs/optimize', {
        faq_data_list: cleaned
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data?.message || error.response.data?.suggestion || 'Failed to optimize FAQs'
        );
      } else if (error.request) {
        throw new Error('Network error while optimizing FAQs');
      } else {
        throw new Error('An unexpected error occurred while optimizing FAQs');
      }
    }
  }

  async updateFAQ(data) {
    // data should contain faq_id and other fields to update
    const response = await this.api.put('/api/faqs/update', data);
    return response.data;
  }

  async deleteFAQ(faqIdList) {
    try {
      const ids = Array.isArray(faqIdList) ? faqIdList.map(id => String(id)) : [];
      if (ids.length === 0) {
        throw new Error('No FAQs selected for deletion');
      }
      const response = await this.api.delete(`/api/faqs/delete`, {
        // axios.delete puts the request body under the `data` key
        data: { faqId_list: ids }
      });
      return response.data;
    } catch (error) {
      // Handle HTTP errors (404, 500, etc.)
      if (error.response) {
        // Server responded with error status
        throw new Error(
          error.response.data?.message ||
          error.response.data?.suggestion ||
          'Failed to delete FAQ'
        );
      } else if (error.request) {
        // Network error
        throw new Error('Network error while deleting FAQ');
      } else {
        // Other error
        throw new Error('An unexpected error occurred while deleting FAQ');
      }
    }
  }

  // Widget management
  async createWidget(widgetType, configuration) {
    const response = await this.api.post('/api/widgets/create', {
      widget_type: widgetType,
      configuration,
    });
    useBillingStore.getState().fetchBillingInfo();
    return response.data;
  }
  async getWidgets() {
    const response = await this.api.get('/api/widgets/list');
    return response.data;
  }
  async updateWidgetSettings(widgetId, settings) {
    const response = await this.api.put(`/api/widgets/${widgetId}/settings`, settings);
    return response.data;
  }

  async getWidgetConfig(widgetId) {
    const response = await this.api.get(`/api/widgets/${widgetId}/config`);
    return response.data;
  }

  // Chat functionality
  async chatQuery(query) {
    const response = await this.api.post('/api/chat/query', {
      query,
    });
    return response.data;
  }

  // Support queries
  async submitSupportQuery(customerEmail, query, name, mobile) {
    const response = await this.api.post('/api/support/submit', {
      customer_email: customerEmail,
      name,
      mobile,
      query,
    });
    useBillingStore.getState().fetchBillingInfo();
    return response.data; //
  }

  async submitReplyAll(queryIds, reply) {
    const response = await this.api.post('/api/support/submit/reply-all', {
      query_ids: queryIds,
      reply: reply
    });
    useBillingStore.getState().fetchBillingInfo();
    return response.data;
  }

  async reWrite(inputText) {
    const response = await this.api.post('/api/utility/rewrite', {
      "input_text": inputText
    });
    return response.data;
  }

  async submitManualResponse(queryId, customerEmail, humanResponse) {
    const response = await this.api.post('/api/support/submit/manual', {
      query_id: queryId,
      customer_email: customerEmail,
      human_response: humanResponse,
    });
    useBillingStore.getState().fetchBillingInfo();
    return response.data;
  }

  async downloadCSVforSupportQueries(requiredStatus, startDate, endDate) {
    const response = await this.api.post('/api/support/export/queries',
      {
        required_status: requiredStatus,
        start_date: startDate || null,
        end_date: endDate || null
      },  //array of required status 
      { responseType: 'blob' }
    );
    return response;
  }

  async getSupportQueries(payload) {
    // payload should contain: start_date, end_date, page, per_page, filters
    const response = await this.api.post('/api/support/queries', payload);
    return response.data;
  }

  async getSupportQueryDetail(queryId) {
    const response = await this.api.get(`/api/support/queries/${queryId}`);
    return response.data;
  }

  async getSupportFilterOptions() {
    const response = await this.api.get('/api/support/filters');
    return response.data;
  }

  // Full website scrape
  async fetchSitemapURLs(sitemapUrl) {
    if (sitemapUrl) {
      const response = await this.api.get(`/api/website/discover?sitemap_url=${sitemapUrl}`);
      return response.data;
    } else {
      const response = await this.api.get(`/api/website/discover`);
      return response.data;
    }
  }

  async createCloudTasks(urlList) {
    const response = await this.api.post('/api/website/create_tasks', {
      "url_list": urlList
    });
    useBillingStore.getState().fetchBillingInfo();
    return response.data;
  }

  async stopCloudTasksJob(jobId) {
    const response = await this.api.delete(`/api/website/jobs/${jobId}`);
    return response.data;
  }

  async trackJobProgress(jobID) {
    const response = await this.api.get(`/api/website/jobs/${jobID}/status`);
    return response.data;
  }

  // for API KEY's
  async getApiKeys() {
    const response = await this.api.get("/api/keys/");
    return response.data;
  }

  async createApiKey(keyName) {
    const response = await this.api.post("/api/keys/", {
      "key_name": keyName
    });
    return response.data;
  }

  async deleteApiKey(keyId) {
    const response = await this.api.delete(`/api/keys/${keyId}`);
    return response.data;
  }

  // Utilities
  // reWrite is already defined above

  // Billing
  async getBillingUsage() {
    const response = await this.api.get('/api/billing/usage');
    return response.data;
  }

  async getInvoices() {
    const response = await this.api.get('/api/razorpay/invoice');
    return response.data;
  }

  async addNoteToSupportQuery(supportQueryId, notes) {
    try {
      const response = await this.api.patch('/api/support/update/notes', {
        support_queryId: supportQueryId,
        notes: notes
      });
      return response.data;
    } catch (error) {
      // Handle HTTP errors (404, 500, etc.)
      if (error.response) {
        // Server responded with error status
        throw new Error(error.response.data.detail?.message || error.response.data.message || 'Failed to add note');
      } else if (error.request) {
        // Network error
        throw new Error('Network error while adding note');
      } else {
        // Other error
        throw new Error('An unexpected error occurred while adding note');
      }
    }
  }

  async updateSupportQuery(supportQueryIds, newStatus) {
    try {
      const response = await this.api.patch('/api/support/update/status', {
        support_queryIds: supportQueryIds,
        status: newStatus
      });
      return response.data;
    } catch (error) {
      // Handle HTTP errors (404, 500, etc.)
      if (error.response) {
        // Server responded with error status
        throw new Error(
          error.response.data.message ||
          error.response.data.suggestion ||
          `Failed to ${newStatus} support query`
        );
      } else if (error.request) {
        // Network error
        throw new Error('Network error while closing support query');
      } else {
        // Other error
        throw new Error('An unexpected error occurred while closing support query');
      }
    }
  }


  // Organization methods
  async createOrganization(formData) {
    const response = await this.api.post('/api/org/create', formData);
    useBillingStore.getState().fetchBillingInfo();
    return response.data;
  }

  async getOrganizationData() {
    const response = await this.api.get('/api/org/');
    return response.data;
  }

  async updateOrgFields(data) {
    const response = await this.api.put('/api/org/', data);
    return response.data;
  }

  async sendTestReport(pdfBlob, reportType = 'test', dateRange = null) {
    const formData = new FormData();
    if (pdfBlob) {
      formData.append('report_pdf', pdfBlob, `${reportType}_report.pdf`);
    }
    formData.append('report_type', reportType);
    if (dateRange) {
      if (dateRange.start_date) formData.append('start_date', dateRange.start_date);
      if (dateRange.end_date) formData.append('end_date', dateRange.end_date);
    }

    const response = await this.api.post('/api/org/send-test-report', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Analytics
  async trackAnalytics(data) {
    const response = await this.api.post('/api/analytics/track', data);
    return response.data;
  }

  async getAnalyticsOverview() {
    const response = await this.api.get('/api/analytics/overview'); return response.data;
  }

  async createOrder(data) {
    const response = await this.api.post('/api/razorpay/create-order', data);
    return response.data;
  }

  async verifyPayment(data) {
    const response = await this.api.post('/api/razorpay/verify-payment', data);
    useBillingStore.getState().fetchBillingInfo();
    useBillingStore.getState().fetchSubscriptionDetails(true);
    return response.data;
  }

  async createSubscription(data) {
    const response = await this.api.post('/api/razorpay/create-subscription', data);
    return response.data;
  }

  async verifySubscriptionPayment(data) {
    const response = await this.api.post('/api/razorpay/verify-payment', data);
    useBillingStore.getState().fetchBillingInfo();
    useBillingStore.getState().fetchSubscriptionDetails(true);
    return response.data;
  }

  async getSubscriptionDetails() {
    const response = await this.api.get('/api/billing/subscription');
    return response.data;
  }

  async cancelSubscription() {
    const response = await this.api.post('/api/razorpay/cancel-subscription');
    return response.data;
  }

  async refundSubscription(data) {
    const response = await this.api.post('/api/razorpay/refund', data);
    return response.data;
  }

  async getPricingPlans() {
    const response = await this.api.get('/api/razorpay/plans');
    return response.data;
  }

  // Dashboard overview
  async getDashboardOverview(params) {
    if (Object.keys(params).length > 0) {
      const response = await this.api.get('/api/dashboard/overview', { params });
      return response.data;
    } else {
      const response = await this.api.get('/api/dashboard/overview');
      return response.data;
    }
  }

  async getDashboardAnalytics(params) {
    if (Object.keys(params).length > 0) {
      const response = await this.api.get('/api/analytics/support/overview', { params });
      return response.data;
    } else {
      const response = await this.api.get('/api/analytics/support/overview');
      return response.data;
    }
  }

  // Health check
  async healthCheck() {
    const response = await this.api.get('/api/health');
    return response.data;
  }

  async createManualSubscription(data) {
    const response = await this.api.post('/api/billing/subscription/manual', data);
    return response.data;
  }

  async reportOverview(params) {
    if (Object.keys(params).length > 0) {
      const response = await this.api.get('/api/analytics/overview', { params });
      return response.data;
    } else {
      const response = await this.api.get('/api/analytics/overview');
      return response.data;
    }
  }

  async aiResponseQuality(params) {
    if (Object.keys(params).length > 0) {
      const response = await this.api.get('/api/analytics/support/ai_res_quality', { params });
      return response.data;
    } else {
      const response = await this.api.get('/api/analytics/support/ai_res_quality');
      return response.data;
    }
  }

  async queryClassification(params) {
    if (Object.keys(params).length > 0) {
      const response = await this.api.get('/api/analytics/support/classification', { params });
      return response.data;
    } else {
      const response = await this.api.get('/api/analytics/support/classification');
      return response.data;
    }
  }

  async sentiment(params) {
    if (Object.keys(params).length > 0) {
      const response = await this.api.get('/api/analytics/support/sentiment', { params });
      return response.data;
    } else {
      const response = await this.api.get('/api/analytics/support/sentiment');
      return response.data;
    }
  }

  async sentimentOverTime(params) {
    if (Object.keys(params).length > 0) {
      const response = await this.api.get('/api/analytics/support/sentiment', { params });
      return response.data;
    } else {
      const response = await this.api.get('/api/analytics/support/sentiment/timeline');
      return response.data;
    }
  }

  async urgency(params) {
    if (Object.keys(params).length > 0) {
      const response = await this.api.get('/api/analytics/support/urgency', { params });
      return response.data;
    } else {
      const response = await this.api.get('/api/analytics/support/urgency');
      return response.data;
    }
  }

  async rootCause(params) {
    if (Object.keys(params).length > 0) {
      const response = await this.api.get('/api/analytics/support/root_cause', { params });
      return response.data;
    } else {
      const response = await this.api.get('/api/analytics/support/root_cause');
      return response.data;
    }
  }

  async ragPerformance(params) {
    if (Object.keys(params).length > 0) {
      const response = await this.api.get('/api/analytics/support/rag_performance', { params });
      return response.data;
    } else {
      const response = await this.api.get('/api/analytics/support/rag_performance');
      return response.data;
    }
  }

  async queryTechnicals(params) {
    if (Object.keys(params).length > 0) {
      const response = await this.api.get('/api/analytics/support/query_technicals', { params });
      return response.data;
    } else {
      const response = await this.api.get('/api/analytics/support/query_technicals');
      return response.data;
    }
  }

  // CSAT Support
  async submitCSATScore(queryId, score, comment) {
    const response = await this.api.post('/api/support/csat/submit', {
      query_id: queryId,
      score: score,
      comment: comment
    });
    return response.data;
  }

  async getCSATScores(params = {}) {
    const response = await this.api.get('/api/support/csat/scores', { params });
    return response.data;
  }

  async getQueryCsat(queryId) {
    const response = await this.api.get(`/api/support/queries/${queryId}/csat`);
    return response.data;
  }

  async updatePriority(supportQueryIds, newPriority) {
    const response = await this.api.patch('/api/support/update/status', {
      support_queryIds: supportQueryIds,
      priority: newPriority
    });
    return response.data;
  }

  async getCSATContext(queryId) {
    // This endpoint will return organization name and logo for the given query_id
    const response = await this.api.get(`/api/support/csat/context/${queryId}`);
    return response.data;
  }

  async logError(message, component = "Frontend", severity = "Medium") {
    // Prevent infinite loops if the logging itself fails
    try {
      const response = await this.api.post('/api/errors/log', null, {
        params: {
          message: message,
          component: component,
          severity: severity,
          source: 'frontend'
        }
      });
      return response.data;
    } catch (e) {
      console.warn("Failed to log error to backend:", e);
    }
  }

  // Instagram Integration API calls
  async getInstagramStatus() {
    const response = await this.api.get('/api/integrations/instagram/auth/status');
    return response.data;
  }

  async submitInstagramAuthCallback(code) {
    const response = await this.api.post('/api/integrations/instagram/auth/callback', { code });
    return response.data;
  }

  async refreshInstagramToken(integrationId) {
    const response = await this.api.put('/api/integrations/instagram/auth/refresh', { integration_id: integrationId });
    return response.data;
  }

  async disconnectInstagram(integrationId) {
    const response = await this.api.delete(`/api/integrations/instagram/auth/${integrationId}`);
    return response.data;
  }

  // Instagram Inbox
  async getInstagramConversations(integrationId, { page = 1, perPage = 15, search = '' } = {}) {
    const params = { page, per_page: perPage };
    if (search) params.search = search;
    const response = await this.api.get(
      `/api/integrations/instagram/${integrationId}/conversations`,
      { params }
    );
    return response.data;
  }

  async getInstagramMessages(integrationId, conversationId, { page = 1, perPage = 30 } = {}) {
    const response = await this.api.get(
      `/api/integrations/instagram/${integrationId}/conversations/${conversationId}/messages`,
      { params: { page, per_page: perPage } }
    );
    return response.data;
  }

  async sendInstagramDM(integrationId, payload) {
    const response = await this.api.post(
      `/api/integrations/instagram/${integrationId}/dm`,
      payload
    );
    return response.data;
  }

  async updateInstagramWebhook(integrationId, action, fields = []) {
    const body = { action };
    if (action === 'subscribe') body.fields = fields;
    const response = await this.api.patch(
      `/api/integrations/instagram/${integrationId}/webhook`,
      body
    );
    return response.data;
  }

  // WooCommerce Integration API calls
  async getWooCommerceStatus() {
    const response = await this.api.get('/api/integrations/woocommerce/status');
    return response.data;
  }

  async getWooCommerceConnectUrl(storeUrl) {
    const response = await this.api.get('/api/integrations/woocommerce/connect', {
      params: { store_url: storeUrl },
    });
    return response.data;
  }

  async connectWooCommerceManual({ store_url, consumer_key, consumer_secret }) {
    const response = await this.api.post('/api/integrations/woocommerce/connect/manual', {
      store_url,
      consumer_key,
      consumer_secret,
    });
    return response.data;
  }

  async triggerWooCommerceSync(integrationId) {
    const response = await this.api.post(`/api/integrations/woocommerce/${integrationId}/sync`);
    return response.data;
  }

  async getWooCommerceSyncStatus(integrationId) {
    const response = await this.api.get(
      `/api/integrations/woocommerce/${integrationId}/sync/status`
    );
    return response.data;
  }

  async getWooCommerceProducts(integrationId, { page = 1, size = 20 } = {}) {
    const response = await this.api.get(
      `/api/integrations/woocommerce/${integrationId}/products`,
      { params: { page, size } }
    );
    return response.data;
  }

  async getWooCommerceCustomers(integrationId, { page = 1, size = 20 } = {}) {
    const response = await this.api.get(
      `/api/integrations/woocommerce/${integrationId}/customers`,
      { params: { page, size } }
    );
    return response.data;
  }

  async disconnectWooCommerce(integrationId, { purgeContent = false } = {}) {
    const response = await this.api.delete(`/api/integrations/woocommerce/${integrationId}`, {
      params: { purge_content: purgeContent },
    });
    return response.data;
  }

}

export default new ApiService();
